import { useEffect, useState } from "react";
import {
  Button,
  ListContext,
  ListContainer,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  Typography,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";
import { ListFooter } from "components";
import {
  LkPersonTypeId,
  ListFilterBase,
  Permission,
  PermissionAction,
  TextingQueueListDto,
  TextingQueueListResource,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import {
  CreateTextingConversationsPageState,
  createTextingConversationsPageRoute,
} from "../CreateTextingConversationsPage/CreateTextingConversationsPage";
import { ScheduledTextsTable } from "./ScheduledTextsTable";

const routeName = "ScheduledTexts";

export const scheduledTextsPageRoute: PageRoute = {
  handle: "Scheduled Texts",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ScheduledTextsPage = () => {
  const navigate = useNavigate();
  const [scheduledTexts, setScheduledTexts] = useState<TextingQueueListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<TextingQueueListDto>("scheduledDate"), isDesc: true }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<TextingQueueListResource>({});
  const [searchFocused, setSearchFocused] = useState(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter,
    setListFilter,
    fetchList: (filter) => {
      ApiService.textingQueue.getList(filter).then(setScheduledTexts);
    },
    fetchTotal: (filter) => {
      setTotal(undefined);
      ApiService.textingQueue.getListTotal(filter).then(setTotal);
    },
  });

  useEffect(() => {
    loadList(listFilter);
    ApiService.textingQueue.getListResource().then(setResource);
  }, []);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{scheduledTextsPageRoute.handle}</Typography>

            {isAuthorized(Permission.Texting, PermissionAction.Create) && (
              <Button
                onClick={() =>
                  navigate(createTextingConversationsPageRoute.getPath(), {
                    state: {
                      personTypeId: LkPersonTypeId.Student,
                      personIds: [],
                      scheduledDate: new Date(),
                    } as CreateTextingConversationsPageState,
                  })
                }
                variant="outlined"
              >
                + New
              </Button>
            )}
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <ScheduledTextsTable
              tableContainerRef={tableContainerRef}
              scheduledTexts={scheduledTexts}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>
      <ListContext.Provider value={{ loadList: () => loadList(listFilter) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
